import { useCallback, useEffect, useRef, useState } from 'react';
import { json } from '../../../api/apiClient';

const POLL_INTERVAL_MS = 2000;

/**
 * Pollt die Lobby-Antwort alle 2 s. Jede Anfrage bekommt eine laufende Nummer; nach `markWrite()`
 * werden Antworten älterer Anfragen verworfen — sonst überschreibt ein noch unterwegs befindlicher
 * Poll den gerade gespeicherten Wert für zwei Sekunden mit dem alten Stand.
 */
export function useLobbyPolling(sessionId) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [failures, setFailures] = useState(0);
  const seqRef = useRef(0);
  const minAcceptedSeqRef = useRef(0);
  const loadedRef = useRef(false);
  const mountedRef = useRef(true);
  const erroredRef = useRef(false);

  const poll = useCallback(async () => {
    const mySeq = ++seqRef.current;
    try {
      const data = await json(`/api/playtest/${sessionId}`);
      if (!mountedRef.current || mySeq < minAcceptedSeqRef.current) return;
      loadedRef.current = true;
      erroredRef.current = false;
      setSession(data);
      setError(null);
      setFailures(0);
    } catch (err) {
      if (!mountedRef.current || mySeq < minAcceptedSeqRef.current) return;
      // 404/403 sind endgültige Antworten (Lobby beendet, kein Mitglied mehr) — sie führen auch
      // nach dem Erstladen in den Fehler-Leerzustand, weil Weiterpollen sie nicht ändert. Alles
      // andere (Netzfehler, 5xx) ist vorübergehend und zählt nur als Fehlschlag in Folge.
      const gone = err?.status === 404 || err?.status === 403;
      if (loadedRef.current && !gone) setFailures((n) => n + 1);
      else { erroredRef.current = true; setError(err); }
    }
  }, [sessionId]);

  useEffect(() => {
    mountedRef.current = true;
    loadedRef.current = false;
    erroredRef.current = false;
    setSession(null); setError(null); setFailures(0);
    // Ohne Router-Remount läuft dieser Effekt bei einem Wechsel der sessionId erneut, während
    // eine Anfrage der alten sessionId noch unterwegs ist — die Anhebung verwirft deren Antwort
    // wie eine Antwort vor markWrite().
    minAcceptedSeqRef.current = seqRef.current + 1;
    poll();
    // Im Fehlerzustand ruht der Takt: die Seite zeigt dann einen Leerzustand, und weitere
    // Anfragen ändern nichts daran. Der Retry-Knopf ruft `refetchNow` und weckt ihn wieder.
    const timer = setInterval(() => { if (!erroredRef.current) poll(); }, POLL_INTERVAL_MS);
    return () => { mountedRef.current = false; clearInterval(timer); };
  }, [poll]);

  const markWrite = useCallback(() => { minAcceptedSeqRef.current = seqRef.current + 1; }, []);

  return { session, error, failures, setSession, markWrite, refetchNow: poll };
}
